import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Translate } from 'react-localize-redux';
import { useNavigate } from 'react-router-dom';
import PulseLoader from 'react-spinners/PulseLoader';
import { Alert } from 'reactstrap';

import { isAfter } from 'date-fns';

import classNames from '../../helpers/classNames';
import useTranslate from '../../hooks/useTranslate';
import { MappedGolfCourseType } from '../../services/apis/golfCourses/types';
import { usePreBook } from '../../services/apis/order/api';
import { createTeeTimeOrderItem } from '../../services/apis/order/utils';
import { useProfile } from '../../services/apis/profile/api';
import { GolfIdVerificationStatus } from '../../services/apis/profile/types';
import {
  getGolfIdVerificationStatus,
  isGolfCourseRequireGolfIdVerification,
} from '../../services/apis/profile/utils';
import useTeeTimes from '../../services/apis/teeTimes/api';
import {
  TeeTimeExtraProductPrebookType,
  TeeTimePrebookMappedType,
  TeeTimeRatePrebookMappedType,
  TeeTimeType,
} from '../../services/apis/teeTimes/types';
import { isEligibleForHighlight, needsTeeTimeHighlight } from '../../services/apis/teeTimes/utils';
import useStore from '../../store/useStore';
import Button from '../Button/Button';
import DatesTable from '../DatesTable/DatesTable';
import GolfIdVerificationFormProvider from '../GolfIdVerification/GolfIdVerificationFormProvider';
import GolfIdVerificationInlineForm from '../GolfIdVerification/GolfIdVerificationInlineForm';
import RatesModal from '../RatesModal/RatesModal';
import TeeTimes from '../TeeTimes/TeeTimes';

interface TeeTimesAvailabilityProps {
  golfCourse: MappedGolfCourseType;
  selectedDate: string;
  shrinkDatesTable?: boolean;
  onShowMap?: () => void;
  mapVisible?: boolean;
}

const TeeTimesAvailability: FC<TeeTimesAvailabilityProps> = props => {
  const {
    golfCourse,
    selectedDate: defaultSelectedDate,
    shrinkDatesTable,
    onShowMap,
    mapVisible,
  } = props;

  const [selectedDate, setSelectedDate] = useState(defaultSelectedDate);
  const [currentTime, setCurrentTime] = useState(() => Date.now());
  const [selectedTeeTime, setSelectedTeeTime] = useState<TeeTimePrebookMappedType | null>(null);

  const navigate = useNavigate();
  const { translate, activeLanguage } = useTranslate();

  const isLoggedIn = useStore(state => state.isLoggedIn);
  const golfhaftetUser = useStore(state => state.golfhaftetUser);
  const openAuthModal = useStore(state => state.openAuthModal);

  const {
    data: availability,
    isFetching: loadingTeeTimes,
    error,
  } = useTeeTimes(golfCourse.golfCourse.id, selectedDate);

  const { data: profile } = useProfile();
  const golfIdVerificationStatus = getGolfIdVerificationStatus(profile);
  const golfIdVerificationRequired =
    golfIdVerificationStatus === GolfIdVerificationStatus.REQUIRED &&
    !!availability &&
    isGolfCourseRequireGolfIdVerification(availability);

  const availableTeeTimes = useMemo(
    () =>
      availability?.teetimes?.filter(teeTime => isAfter(Date.parse(teeTime.dateTime), currentTime)),
    [availability, currentTime]
  );
  const noAvailableTeeTimes = !availableTeeTimes?.length;

  const informationFromGolfClub = useMemo(() => availability?.information, [availability]);

  const handleTeeTimeSelect = useCallback(
    (selectedTeeTime: TeeTimeType) => {
      setSelectedTeeTime(
        availability
          ? {
              ...selectedTeeTime,
              currency: availability.currency.name,
              extraProducts: availability?.extraProducts || null,
            }
          : null
      );
    },
    [availability]
  );

  const closeRateModal = () => {
    setSelectedTeeTime(null);
    reset();
  };

  const {
    mutateAsync: prebookTeeTimeOrderItem,
    isLoading: isPrebooking,
    error: prebookError,
    reset,
  } = usePreBook();

  const prebookTeeTime = async (
    selectedRates: TeeTimeRatePrebookMappedType[],
    selectedExtraProducts: TeeTimeExtraProductPrebookType[],
    goToCheckout = false
  ) => {
    if (!isLoggedIn && !golfhaftetUser?.proceedAsGuest) {
      openAuthModal(true);
      return;
    }

    if (!selectedTeeTime) {
      return;
    }

    const teeTimeOrderItem = createTeeTimeOrderItem(
      golfCourse,
      selectedTeeTime,
      selectedRates,
      selectedExtraProducts
    );

    // fixme
    // @ts-ignore
    await prebookTeeTimeOrderItem({
      booking: teeTimeOrderItem,
      guestCheckout: golfhaftetUser?.email
        ? {
            email: golfhaftetUser.email,
            firstName: golfhaftetUser.firstName,
            lastName: golfhaftetUser.lastName,
            golfamoreCardNumber: golfhaftetUser.cardNr,
          }
        : null,
    });

    // We will only come here if the preebook is successful
    setSelectedTeeTime(null);

    if (goToCheckout) {
      navigate(`/${activeLanguage.code}/booking/confirm/`);
    }
  };

  const shouldHighlight = useMemo(
    () => !!availableTeeTimes?.length && needsTeeTimeHighlight(availableTeeTimes),
    [availableTeeTimes]
  );

  useEffect(() => {
    let intervalId: number | undefined;
    const nowDate = new Date();
    const oneMinute = 60 * 1000;
    const updateCurrentTime = () => {
      const now = new Date();
      now.setMilliseconds(0);

      setCurrentTime(now.getTime());
    };
    const timeoutId = window.setTimeout(() => {
      intervalId = window.setInterval(updateCurrentTime, oneMinute);

      updateCurrentTime();
    }, oneMinute - nowDate.getSeconds() * 1000 - nowDate.getMilliseconds());

    return () => {
      window.clearTimeout(timeoutId);
      window.clearInterval(intervalId);
    };
  }, []);

  useEffect(() => {
    setSelectedDate(defaultSelectedDate);
  }, [defaultSelectedDate]);

  return (
    <>
      <div className="fade show position-relative" style={{ minHeight: 230 }}>
        <div className={classNames(loadingTeeTimes && 'opacity-25')}>
          <DatesTable
            golfCourse={golfCourse}
            selectedDate={selectedDate}
            onSelectedDateChange={setSelectedDate}
            shrink={shrinkDatesTable}
          />

          {!loadingTeeTimes && (error || noAvailableTeeTimes) && (
            <Alert color="warning" className="my-3">
              {translate('default.NoTimesFound')}
              {/* {error?.message} */}
            </Alert>
          )}

          {!!informationFromGolfClub && !loadingTeeTimes && !error && (
            <Alert color="info" className="my-3">
              {informationFromGolfClub}
            </Alert>
          )}

          {!!availableTeeTimes?.length && (
            <TeeTimes
              items={availableTeeTimes}
              currency={availability?.currency.name ?? ''}
              onSelect={handleTeeTimeSelect}
              shouldHighlight={shouldHighlight}
              onShowMap={onShowMap}
              mapVisible={mapVisible}
            />
          )}

          {onShowMap && !mapVisible && !availableTeeTimes?.length && (
            <div className="text-end">
              <Button primary onClick={onShowMap} className="mt-3">
                <Translate id="District.ShowMap" />
              </Button>
            </div>
          )}
        </div>

        <div
          className={classNames(
            'position-absolute top-50 start-50 translate-middle fade pe-none',
            loadingTeeTimes && 'show'
          )}
        >
          <PulseLoader size={8} />
        </div>
      </div>
      {selectedTeeTime && (
        <RatesModal
          teetime={selectedTeeTime}
          informer={
            golfIdVerificationRequired ? (
              <Alert color={'danger'}>
                <GolfIdVerificationFormProvider>
                  <GolfIdVerificationInlineForm />
                </GolfIdVerificationFormProvider>
              </Alert>
            ) : (
              shouldHighlight &&
              isEligibleForHighlight(selectedTeeTime) && (
                <Alert color={'success'}>{translate('teeTimeSellOutInformer')}</Alert>
              )
            )
          }
          course={golfCourse}
          prebookBooking={prebookTeeTime}
          onClose={closeRateModal}
          inProgress={isPrebooking}
          // @ts-expect-error TODO untyped
          error={prebookError?.message || prebookError}
          // isGolfhaftetRequest={isGolfhaftetRequest}
          disabled={golfIdVerificationRequired}
        />
      )}
    </>
  );
};

export default TeeTimesAvailability;
